import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';
import { getToken } from '@/utils/auth.util';
import { reportHandledError } from '@/lib/frontend-error-reporter';
import { ROUTES } from '../constants/path';
import { SNS_SIGN_IN_URL, TOKEN_REFRESH_URL, USER_LOGIN_URL } from '../constants/url';
import { refreshAccessToken } from './token-refresh';

let tokenRefreshRequest: Promise<string> | null = null;

type RetryableAxiosRequestConfig = AxiosRequestConfig & { _retry?: boolean };

const requestPath = (url?: string): string => {
  if (!url) return '/';

  try {
    const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
    return new URL(url, origin).pathname || '/';
  } catch {
    return url.split(/[?#]/, 1)[0].slice(0, 500) || '/';
  }
};

const authorizationHeader = (headers?: AxiosRequestConfig['headers']): string | undefined => {
  if (!headers) return undefined;

  const value = typeof headers.get === 'function' ? headers.get('Authorization') : headers.Authorization;
  return typeof value === 'string' ? value : undefined;
};

const reportUnauthorizedRequest = (request?: RetryableAxiosRequestConfig): void => {
  reportHandledError('axios request unauthorized', {
    method: (request?.method || 'GET').toUpperCase(),
    path: requestPath(request?.url),
    status: 401
  });
};

const reportRefreshFailureWhenTransportDidNot = (error: unknown): void => {
  // 4xx/5xx·network AxiosError 는 XHR 계측기가 이미 보고한다. 단 XHR 계측이 의도적으로
  // 넘기는 401 과 HTTP 200 본문 오류·로컬 토큰 누락만 여기서 되살린다.
  if (!axios.isAxiosError(error) || error.response?.status === 401) {
    const message = error instanceof Error && error.message === 'No refresh token' ? 'auth recovery unavailable: no-refresh-token' : 'auth recovery failed: refresh-failed';
    reportHandledError(message, {
      method: 'POST',
      path: TOKEN_REFRESH_URL,
      status: 401
    });
  }
};

const isTokenlessRequest = (url?: string): boolean => {
  if (!url) return false;
  return url.includes('/unrestricted/') || url.endsWith(USER_LOGIN_URL) || url.endsWith(SNS_SIGN_IN_URL);
};

const refreshAccessTokenOnce = (): Promise<string> => {
  if (!tokenRefreshRequest) {
    tokenRefreshRequest = refreshAccessToken()
      .catch((error) => {
        // 공유 Promise 에 한 번만 붙여 동시 401 요청도 재발급 실패를 한 건만 보고한다.
        reportRefreshFailureWhenTransportDidNot(error);
        throw error;
      })
      .finally(() => {
        tokenRefreshRequest = null;
      });
  }

  return tokenRefreshRequest;
};

axios.defaults.baseURL = process.env.NEXT_PUBLIC_BASE_URL;
axios.defaults.timeout = 10000;

axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token?.accessToken && !isTokenlessRequest(config.url)) {
    config.headers = config.headers || {};
    config.headers.Authorization = token.accessToken;
  }
  return config;
});

axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config as RetryableAxiosRequestConfig | undefined;
    const hasAccessToken = Boolean(getToken()?.accessToken);
    const shouldRefreshToken = originalRequest && !isTokenlessRequest(originalRequest.url);

    if (error.response?.status === 401 && shouldRefreshToken && hasAccessToken && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const currentAccessToken = getToken()?.accessToken;
        const requestAccessToken = authorizationHeader(originalRequest.headers);
        const newToken = currentAccessToken && requestAccessToken && currentAccessToken !== requestAccessToken ? currentAccessToken : await refreshAccessTokenOnce();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = newToken;
        return axios(originalRequest);
      } catch (err) {
        if (typeof window !== 'undefined') {
          window.location.replace(ROUTES.getRedirectMiddleware({ type: 'TOKEN_EXPIRED' }));
        }
        return Promise.reject(err);
      }
    }

    if (error.response?.status === 401) {
      // 최초 401은 위에서 복구한다. 여기 도달하는 것은 재시도도 401이거나,
      // 로그인·무토큰 요청처럼 재발급 대상이 아닌 최종 실패다.
      reportUnauthorizedRequest(originalRequest);
    }

    return Promise.reject(error);
  }
);

export default axios;
